import React, { useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import {
  EmptyState,
  PaginationWrapper,
  PaginationWrapperRefProps,
  PopoverFilterEntry,
  Section,
  TableColumn,
} from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { RelativeTime } from '~/elements';
import { EventRepository, parseEvent } from '~/repositories';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

import { CardProps } from './Card';

interface Params {
  id: string;
}

// Card event types
const CARD_EVENT_TYPES = ['cards.card.created', 'cards.card.activated', 'cards.card.paused', 'cards.card.inactive'];

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const Action = styled.div`
  text-align: right;
`;

const JSONButton = styled(Button)`
  &:hover {
    &:after {
      background: ${({ theme }) => theme.gray.blendToBackground(75)};
    }
  }
`;

const getCardEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  if (parts.length < 3) return formatString(eventType);

  const action = parts[2];
  switch (action) {
    case 'created':
      return 'Card Created';
    case 'activated':
      return 'Card Activated';
    case 'paused':
      return 'Card Paused';
    case 'inactive':
      return 'Card Deactivated';
    default:
      return formatString(action);
  }
};

export const PageCardEvents: React.FC = () => {
  const { id: cardId } = useParams<keyof Params>() as Params;
  useOutletContext<CardProps>(); // Ensures we're in the card context
  const paginationRef = useRef<PaginationWrapperRefProps>(null);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    // Fetch events filtered by object_id (card ID) server-side
    return EventRepository.getAll({
      ...params,
      objectIds: [cardId],
    }).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              size="tiny"
              variant="muted"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: CARD_EVENT_TYPES.map((eventType) => ({
        label: getCardEventLabel(eventType),
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: TableColumn[] = [
    {
      Header: 'Event',
      accessor: 'eventType',
      width: 'minmax(200px, auto)',
      Cell: (current) => (
        <EventMeta>
          <EventType>{getCardEventLabel(current.value)}</EventType>
          <EventDetail>
            <span>{current.value}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      width: 'minmax(140px, 180px)',
      Cell: (current) => <RelativeTime timestamp={current.value} />,
    },
    {
      Header: '',
      accessor: 'action',
      width: '80px',
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <PaginationWrapper
            tableId="card-events"
            fetch={handleFetch}
            columns={columns}
            filter={filter}
            ref={paginationRef}
            empty={
              <EmptyState headline="No events yet" text="Events will appear here when card status changes occur." />
            }
          />
        </Section>
      </Inner>
    </>
  );
};
